import { faBox, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useCart } from 'src/contexts/CartContext';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { CartItem } from 'src/models/cart/_types';
import { formatPaymentOptionDescription } from 'src/models/cart/utils';
import { ProductVariant } from 'src/models/product/_types';
import {
  getMaxAllowedQuantity,
  getStockAvailabilityMessage,
} from 'src/models/product/utils';
import { fetchUserDependents } from 'src/models/user/user';
import { formatCurrency } from 'src/utils/numberFormatter';
import toast from 'src/utils/toast';

interface CartItemRowProps {
  cartItem: CartItem;
  cartItemIndex: number;
}

export const CartItemRow: React.FC<CartItemRowProps> = ({
  cartItem,
  cartItemIndex,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'cart.item' });
  const { t: tRoot } = useTranslation();
  const { cart, removeFromCart, updateQuantity } = useCart();
  const { session, user } = useOptionalSession();
  const currency = cart?.currency || session?.defaultCurrency || 'usd';
  const [localQuantity, setLocalQuantity] = useState(
    cartItem.quantity.toString()
  );
  const [stockMessage, setStockMessage] = useState<string | null>(null);

  const cartItemUserId = cartItem.userId;
  const currentUserId = user?.id;
  const isForOtherUser =
    !!cartItemUserId &&
    !!currentUserId &&
    String(cartItemUserId) !== String(currentUserId);

  const { data: dependentsData } = useQuery({
    queryKey: ['users', 'dependents', currentUserId],
    queryFn: () => fetchUserDependents({ id: currentUserId!, perPage: 100 }),
    enabled: isForOtherUser,
  });

  const dependentName = isForOtherUser
    ? dependentsData?.dependents.find(
        (dep) => String(dep.dependentId) === String(cartItemUserId)
      )?.dependentName
    : undefined;

  const purchaserDisplayName = isForOtherUser ? dependentName : undefined;

  // Update local quantity when cart item quantity changes
  useEffect(() => {
    setLocalQuantity(cartItem.quantity.toString());
  }, [cartItem.quantity]);

  const handleRemove = () => {
    removeFromCart(cartItemIndex);
  };

  const handleQuantityChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    // Allow empty or incomplete input temporarily for user experience
    setLocalQuantity(value);
  };

  const handleQuantityBlur = async () => {
    const newQuantity = parseInt(localQuantity, 10);

    // Reset to actual quantity if invalid
    if (isNaN(newQuantity) || newQuantity <= 0) {
      setLocalQuantity(cartItem.quantity.toString());
      setStockMessage(null);
      return;
    }

    // Check stock limits only for product variants with inventory tracking
    if (
      cartItem.itemType === 'ProductVariant' &&
      cartItem.item &&
      'trackInventory' in cartItem.item
    ) {
      const variant = cartItem.item as unknown as ProductVariant;
      const maxAllowedQuantity = getMaxAllowedQuantity(
        variant,
        cart?.locationId,
        cart,
        cartItemIndex
      );

      if (newQuantity > maxAllowedQuantity) {
        // Revert to original quantity and show error
        setLocalQuantity(cartItem.quantity.toString());

        // Get and show stock message
        const message = getStockAvailabilityMessage(
          variant,
          cart?.locationId,
          cart,
          cartItemIndex
        );

        if (maxAllowedQuantity === 0) {
          toast.error(t('outOfStock'));
          setStockMessage('Out of stock');
        } else {
          const availableText =
            maxAllowedQuantity === 1
              ? t('onlyOneAvailable')
              : t('onlyXAvailable', { count: maxAllowedQuantity });
          toast.error(availableText);
          setStockMessage(message);
        }
        return;
      } else {
        // Clear any existing stock message if quantity is valid
        setStockMessage(null);
      }
    }

    // Update quantity if valid
    if (newQuantity !== cartItem.quantity) {
      await updateQuantity(cartItemIndex, newQuantity);
    }
  };

  const formatPrice = (price: number) => {
    return formatCurrency(price, { currency, cents: true });
  };

  const itemName = cartItem.item?.name || t('unknownItem');
  const itemPrice = cartItem.paymentOption?.amount || 0;
  const itemImage =
    cartItem.item?.imageUrl || (cartItem.item as any)?.product?.imageUrl || '';

  // Only show quantity controls for product variants and products
  const supportsQuantity =
    cartItem.itemType === 'ProductVariant' ||
    cartItem.itemType === 'Exercisecom::Product';

  // Format subscription description (only the interval, not the price)
  const getSubscriptionDescription = () => {
    const option = cartItem.paymentOption;
    if (!option || option.type !== 'subscription') return null;

    // If the payment option has a name, use it
    if (option.name) return option.name;

    // Otherwise, format based on interval using shared utility
    return formatPaymentOptionDescription(option, t, { onlyInterval: true });
  };

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      py={2}
      sx={{
        '&:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Avatar
          src={itemImage}
          sx={{
            borderRadius: 1,
            width: 60,
            height: 60,
            bgcolor: 'grey.100',
          }}
        >
          <Icon
            icon={faBox}
            color="disabled"
            size="small"
          />
        </Avatar>

        <Box flex={1} minWidth={0}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              hyphens: 'auto',
            }}
          >
            {itemName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatPrice(itemPrice)}{supportsQuantity ? ` ${t('each')}` : ''}
          </Typography>
          {cartItem.paymentOption?.type === 'subscription' && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="primary">
                {getSubscriptionDescription()}
              </Typography>
            </Stack>
          )}
          {purchaserDisplayName && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                {tRoot('Buy for')}: {purchaserDisplayName}
              </Typography>
            </Stack>
          )}
          {/* Stock availability message */}
          {stockMessage && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {stockMessage}
            </Typography>
          )}
        </Box>
      </Box>
      {/* Controls Row - Quantity Controls & Remove Button */}
      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
        {/* Quantity Controls - only for products */}
        {supportsQuantity && (
          <Box display="flex" alignItems="center" gap={1}>
            <TextField
              size="small"
              type="number"
              value={localQuantity}
              onChange={handleQuantityChange}
              onBlur={handleQuantityBlur}
              InputProps={{
                inputProps: {
                  min: 1,
                  style: {
                    textAlign: 'center',
                    width: '60px',
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                },
              }}
            />
          </Box>
        )}

        <IconButton size="small" color="error" onClick={handleRemove}>
          <Icon
            icon={faTrash}
            size="xsmall"
          />
        </IconButton>
      </Box>
    </Box>
  );
};
